import React, { useState, useEffect, useRef } from "react";
import { axiosPrivate } from "../../common/axiosPrivate";
import Button from "../../UI/Button";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import useAuth from "../../hooks/useAuth";
import { Link } from "react-router-dom";
import QuoteCard from "../QuoteCard";
import {
  ClipboardCopyIcon,
  DownloadIcon,
  CheckIcon,
  SparklesIcon,
  BookmarkIcon,
  TrashIcon,
  LightningBoltIcon,
  CollectionIcon,
  PencilAltIcon,
  ChevronDownIcon,
  XIcon,
  RefreshIcon,
  DotsVerticalIcon,
  CreditCardIcon,
  InformationCircleIcon,
  ChatIcon,
  DocumentTextIcon,
} from "@heroicons/react/outline";

const MODELS = [
  { id: "anthropic/claude-3.7-sonnet", name: "Claude 3.7 Sonnet" },
  { id: "meta-llama/llama-4-maverick", name: "Llama 4 Maverick" },
  { id: "google/gemini-2.5-pro-preview-03-25", name: "Gemini 2.5 Pro" },
  { id: "google/gemini-2.0-flash-001", name: "Gemini 2.0 Flash" },
  { id: "anthropic/claude-3-haiku", name: "Claude 3 Haiku" },
  { id: "openai/gpt-4o", name: "GPT-4o" },
  { id: "openai/gpt-4.1-mini", name: "GPT-4.1 Mini" },
  { id: "google/gemini-2.0-flash-lite-001", name: "Gemini 2.0 Flash Lite" },
];

const RECIPES = [
  {
    id: "show-notes",
    name: "Generate Show Notes",
    model_id: "google/gemini-2.0-flash-001",
    description: "Create detailed show notes with timestamps, key topics, and highlights",
  },
  {
    id: "twitter-thread",
    name: "Create Twitter Thread",
    model_id: "google/gemini-2.0-flash-001",
    description: "Generate a series of connected tweets highlighting the episode's best insights",
  },
  {
    id: "blog-outline",
    name: "Draft Blog Post Outline",
    model_id: "google/gemini-2.0-flash-001",
    description: "Create a structured outline for a blog post based on the episode's content",
  },
  {
    id: "key-quotes",
    name: "Extract Key Quotes",
    model_id: "google/gemini-2.0-flash-001",
    description: "Pull out the most impactful and quotable moments from the episode",
  },
  {
    id: "email-teaser",
    name: "Write Email Teaser",
    model_id: "google/gemini-2.0-flash-001",
    description: "Craft an engaging email preview to promote the episode to subscribers",
  },
  {
    id: "links-mentions",
    name: "Extract Links & Mentions",
    model_id: "google/gemini-2.0-flash-001",
    description: "Identify and list all external links, tools, books, or people mentioned in the episode",
  },
  {
    id: "generate-titles",
    name: "Suggest Alternative Titles",
    model_id: "google/gemini-2.0-flash-001",
    description: "Generate 5 alternative, SEO-friendly titles for the episode",
  },
  {
    id: "social-posts-linkedin-facebook",
    name: "Create LinkedIn & Facebook Posts",
    model_id: "google/gemini-2.0-flash-001",
    description: "Draft 5 engaging posts suitable for LinkedIn and Facebook, based on the episode's content",
  },
  {
    id: "companion-ebook",
    name: "Create Companion Ebook",
    model_id: "google/gemini-2.0-flash-001",
    description:
      "Generate a comprehensive ebook from the episode transcript, including chapters, key takeaways, and summaries.",
  },
];

export default function Repurpose({ selectedEpisodeState }) {
  const { auth, refreshAIMessageCredits } = useAuth();
  const [activeControlTab, setActiveControlTab] = useState("recipes");
  const [activeContentTab, setActiveContentTab] = useState("content");
  const [customPrompt, setCustomPrompt] = useState("");
  const [generatedContent, setGeneratedContent] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [savedPrompts, setSavedPrompts] = useState([]);
  const [promptName, setPromptName] = useState("");
  const [isSavingPrompt, setIsSavingPrompt] = useState(false);
  const [showSavedPrompts, setShowSavedPrompts] = useState(false);
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [originalContent, setOriginalContent] = useState("");
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const [recipeCosts, setRecipeCosts] = useState({});
  const [customPromptCost, setCustomPromptCost] = useState(null);
  const [isLoadingCosts, setIsLoadingCosts] = useState(false);

  useEffect(() => {
    const fetchSavedPrompts = async () => {
      try {
        const response = await axiosPrivate.get("/repurpose/user/saved_prompts");
        setSavedPrompts(response.data || []);
      } catch (err) {
        console.error("Failed to fetch saved prompts:", err);
      }
    };

    const fetchRecipeCosts = async () => {
      setIsLoadingCosts(true);
      try {
        const response = await axiosPrivate.get("/repurpose/costs");
        if (response.data && response.data.costs) {
          setRecipeCosts(response.data.costs.recipes || {});
          setCustomPromptCost(response.data.costs.custom_prompt || null);
        }
      } catch (err) {
        console.error("Failed to fetch repurpose costs:", err);
      } finally {
        setIsLoadingCosts(false);
      }
    };

    fetchSavedPrompts();
    fetchRecipeCosts();
  }, []);

  useEffect(() => {
    let timer;
    if (showCopySuccess) {
      timer = setTimeout(() => setShowCopySuccess(false), 2000);
    }
    return () => clearTimeout(timer);
  }, [showCopySuccess]);

  useEffect(() => {
    let timer;
    if (showSaveSuccess) {
      timer = setTimeout(() => setShowSaveSuccess(false), 2000);
    }
    return () => clearTimeout(timer);
  }, [showSaveSuccess]);

  useEffect(() => {
    setGeneratedContent("");
    setOriginalContent("");
    setError("");
    setIsLoading(false);
    setIsEditMode(false);
    setActiveControlTab("recipes");
    setActiveContentTab("content");
    setCustomPrompt("");
    setShowCopySuccess(false);
    setShowSaveSuccess(false);
  }, [selectedEpisodeState?.episode?.id, selectedEpisodeState?.episode?.is_transcribed]);

  const isEpisodeReady = selectedEpisodeState?.episode?.is_transcribed && selectedEpisodeState?.episode?.is_summarized;
  const episodeId = selectedEpisodeState?.episode?.id;
  const episodeTitle = selectedEpisodeState?.episode?.title || "episode";

  const handleRecipeClick = async (recipeId) => {
    if (!isEpisodeReady) {
      setError("Episode must be transcribed and summarized first.");
      return;
    }

    setIsLoading(true);
    setError("");
    setGeneratedContent("");
    setIsEditMode(false);

    try {
      const recipe = RECIPES.find((r) => r.id === recipeId);
      if (!recipe) {
        throw new Error("Recipe not found");
      }

      const response = await axiosPrivate.post(`/repurpose/episodes/${episodeId}/repurpose`, {
        model_id: recipe.model_id,
        recipe_id: recipeId,
      });

      const content = response.data.repurposed_content;
      setGeneratedContent(content);
      setOriginalContent(content);

      await refreshAIMessageCredits();
    } catch (err) {
      console.error("Error generating content:", err);
      setError(err.response?.data?.detail || "Failed to generate content. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCustomPromptSubmit = async () => {
    if (!customPrompt.trim()) {
      setError("Please enter a prompt.");
      return;
    }

    if (!isEpisodeReady) {
      setError("Episode must be transcribed and summarized first.");
      return;
    }

    setIsLoading(true);
    setError("");
    setGeneratedContent("");
    setIsEditMode(false);

    try {
      const response = await axiosPrivate.post(`/repurpose/episodes/${episodeId}/repurpose`, {
        model_id: "google/gemini-2.0-flash-001",
        custom_prompt: customPrompt,
      });

      const content = response.data.repurposed_content;
      setGeneratedContent(content);
      setOriginalContent(content);

      await refreshAIMessageCredits();
    } catch (err) {
      console.error("Error generating content:", err);
      setError(err.response?.data?.detail || "Failed to generate content. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(generatedContent);
    setShowCopySuccess(true);
  };

  const handleDownload = () => {
    const element = document.createElement("a");
    const file = new Blob([generatedContent], { type: "text/markdown" });
    element.href = URL.createObjectURL(file);
    element.download = `${episodeTitle.replace(/[^a-z0-9]/gi, "_").toLowerCase()}_repurposed.md`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleSavePrompt = async () => {
    if (!customPrompt.trim() || !promptName.trim()) {
      setError("Please enter both a prompt and a name for the prompt.");
      return;
    }

    setIsSavingPrompt(true);
    setError("");

    try {
      const response = await axiosPrivate.post("/repurpose/user/saved_prompts", {
        name: promptName,
        prompt_text: customPrompt,
      });

      setSavedPrompts([...savedPrompts, response.data]);
      setPromptName("");
      setShowSaveSuccess(true);
    } catch (err) {
      console.error("Failed to save prompt:", err);
      setError(err.response?.data?.detail || "Failed to save prompt. Please try again.");
    } finally {
      setIsSavingPrompt(false);
    }
  };

  const handleLoadPrompt = (prompt) => {
    setCustomPrompt(prompt.prompt_text);
    setShowSavedPrompts(false);
  };

  const handleDeletePrompt = async (promptId) => {
    try {
      await axiosPrivate.delete(`/repurpose/user/saved_prompts/${promptId}`);
      setSavedPrompts(savedPrompts.filter((p) => p.id !== promptId));
    } catch (err) {
      console.error("Failed to delete prompt:", err);
    }
  };

  const toggleEditMode = () => {
    if (isEditMode) {
      setIsEditMode(false);
    } else {
      setIsEditMode(true);
    }
  };

  const revertChanges = () => {
    setGeneratedContent(originalContent);
    setIsEditMode(false);
  };

  if (!isEpisodeReady) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 flex flex-col items-center justify-center text-center">
      <SparklesIcon className="h-12 w-12 text-primary-400 mb-4" />
      <h3 className="text-lg font-medium text-neutral-700 mb-2">Repurpose Content</h3>
      <p className="text-neutral-500 text-sm mb-4 max-w-md">
        This episode needs to be fully transcribed and summarized before you can repurpose its content. Click the{" "}
        <span className="inline-flex items-center text-secondary-600 font-medium border rounded-lg p-1">
        <LightningBoltIcon className="h-4 w-4 mr-1" />
        Podscribe
        </span>
        {" "}button.
      </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 2xl:grid-cols-3 gap-6">
      <div className="lg:col-span-1 bg-white rounded-lg shadow-sm p-6 flex flex-col space-y-4">
        <div className="bg-neutral-50 rounded-md border border-neutral-200 p-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-neutral-700 flex items-center">
              <CreditCardIcon className="h-4 w-4 text-primary-400 mr-1.5" />
              Available AI Credits
            </h4>
            <Link to="/subscription" className="text-xs text-primary-600 hover:text-primary-800 font-medium">
              Get more
            </Link>
          </div>
          <div className="space-y-1.5">
            <div className="flex justify-between items-center text-xs">
              <span className="text-neutral-500">From subscription:</span>
              <span className="font-semibold">{auth?.user?.ai_messages || 0}</span>
            </div>
            <div className="flex justify-between items-center text-xs">
              <span className="text-neutral-500">Top-up credits:</span>
              <span className="font-semibold">{auth?.user?.ai_topup_messages || 0}</span>
            </div>
            <div className="pt-1.5 mt-1.5 border-t border-neutral-200 flex justify-between items-center">
              <span className="text-sm font-medium text-neutral-700">Total available:</span>
              <span
                className={`text-sm font-bold ${
                  (auth?.user?.ai_messages || 0) + (auth?.user?.ai_topup_messages || 0) < 5
                    ? "text-secondary-700"
                    : "text-secondary-500"
                }`}>
                {(auth?.user?.ai_messages || 0) + (auth?.user?.ai_topup_messages || 0)}
              </span>
            </div>
          </div>
          {(auth?.user?.ai_messages || 0) + (auth?.user?.ai_topup_messages || 0) < 5 && (
            <p className="text-xs text-secondary-700 mt-2 flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3.5 w-3.5 mr-1"
                viewBox="0 0 20 20"
                fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              You're running low on credits
            </p>
          )}
        </div>

        <div className="border-b border-neutral-200">
          <nav className="-mb-px flex space-x-6" aria-label="Tabs">
            <button
              onClick={() => setActiveControlTab("recipes")}
              className={`group inline-flex items-center py-3 px-1 border-b-2 font-medium text-sm
                          ${
                            activeControlTab === "recipes"
                              ? "border-primary-500 text-primary-600"
                              : "border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300"
                          }`}>
              <CollectionIcon
                className={`-ml-0.5 mr-2 h-5 w-5 ${
                  activeControlTab === "recipes" ? "text-primary-500" : "text-neutral-400 group-hover:text-neutral-500"
                }`}
              />
              <span>Recipes</span>
            </button>
            <button
              onClick={() => setActiveControlTab("custom")}
              className={`group inline-flex items-center py-3 px-1 border-b-2 font-medium text-sm
                          ${
                            activeControlTab === "custom"
                              ? "border-primary-500 text-primary-600"
                              : "border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300"
                          }`}>
              <PencilAltIcon
                className={`-ml-0.5 mr-2 h-5 w-5 ${
                  activeControlTab === "custom" ? "text-primary-500" : "text-neutral-400 group-hover:text-neutral-500"
                }`}
              />
              <span>Custom Prompt</span>
            </button>
          </nav>
        </div>

        <div className="flex-grow">
          {activeControlTab === "recipes" && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-neutral-700">Repurposing Recipes</h4>
                <div className="flex items-center text-xs text-neutral-500">
                  <InformationCircleIcon className="h-4 w-4 mr-1 text-primary-400" />
                  <span>Select a recipe to generate content</span>
                </div>
              </div>
              <p className="text-xs text-neutral-500">Choose from our pre-built recipes optimized for podcasters.</p>

              <div className="overflow-hidden">
                <div className="space-y-2">
                  {RECIPES.map((recipe) => (
                    <div key={recipe.id} className="relative">
                      <button
                        onClick={() => handleRecipeClick(recipe.id)}
                        disabled={isLoading}
                        className="w-full flex items-center justify-between text-left px-3.5 py-2.5 bg-white border border-neutral-300 hover:border-primary-400 hover:bg-primary-50 rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed group">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 mr-3 text-primary-600">
                            <SparklesIcon className="h-5 w-5" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-neutral-800 group-hover:text-neutral-900">
                              {recipe.name}
                            </p>
                            <p className="text-xs text-neutral-500 group-hover:text-neutral-600 mt-0.5 max-w-[200px]">
                              {recipe.description}
                            </p>
                          </div>
                        </div>
                        <div className="ml-4 pl-4 border-l border-neutral-200">
                          <p className="text-xs font-medium text-primary-600 text-center whitespace-nowrap">
                            {!isLoadingCosts && recipeCosts[recipe.id] !== undefined
                              ? `${recipeCosts[recipe.id]} credits`
                              : isLoadingCosts
                              ? "..."
                              : "—"}
                          </p>
                        </div>
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {isLoading && (
                <div className="text-sm text-primary-600 flex items-center justify-center py-2 space-x-2">
                  <svg
                    className="animate-spin h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Generating recipe...</span>
                </div>
              )}
            </div>
          )}

          {activeControlTab === "custom" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-base font-medium text-neutral-800">Custom Prompt</h4>
                <Button
                  type="button"
                  onClick={handleCustomPromptSubmit}
                  disabled={isLoading || !customPrompt.trim()}
                  variant="primary"
                  className="inline-flex items-center justify-center px-3 py-1.5 text-sm">
                  {isLoading ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Generating...</span>
                    </>
                  ) : (
                    <>
                      <LightningBoltIcon className="h-5 w-5 mr-2 -ml-1" />
                      <div className="flex flex-col items-start">
                        <span>Generate</span>
                        {!isLoadingCosts && customPromptCost !== null && (
                          <span className="text-xs opacity-90">{customPromptCost} credits</span>
                        )}
                      </div>
                    </>
                  )}
                </Button>
              </div>
              <p className="text-sm text-neutral-500">
                Craft your own instructions for the AI using context from the episode.
              </p>
              <textarea
                id="custom-prompt"
                rows={6}
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                disabled={isLoading}
                className="block w-full rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-neutral-50"
                placeholder="e.g., 'Create a LinkedIn post summarizing the key takeaways' or 'Write 5 tweet ideas based on the discussion'"
              />

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-neutral-700">Saved Prompts</label>
                  <Button
                    type="button"
                    onClick={() => setShowSavedPrompts(!showSavedPrompts)}
                    disabled={isLoading || savedPrompts.length === 0}
                    variant="secondary"
                    size="sm"
                    className="inline-flex items-center justify-center px-2.5 py-1 text-xs">
                    <BookmarkIcon className="h-4 w-4 mr-1.5" />
                    {savedPrompts.length > 0 ? `Load (${savedPrompts.length})` : "None saved"}
                    {savedPrompts.length > 0 && (
                      <ChevronDownIcon
                        className={`h-4 w-4 ml-1.5 transform transition-transform duration-150 ${
                          showSavedPrompts ? "rotate-180" : ""
                        }`}
                      />
                    )}
                  </Button>
                </div>

                {showSavedPrompts && (
                  <div className="bg-neutral-50 border border-neutral-200 rounded-md shadow-sm max-h-48 overflow-y-auto scrollbar-thin scrollbar-thumb-neutral-300 hover:scrollbar-thumb-neutral-400 scrollbar-track-neutral-100">
                    {savedPrompts.length > 0 ? (
                      <ul className="py-1 divide-y divide-neutral-100">
                        {savedPrompts.map((prompt) => (
                          <li
                            key={prompt.id}
                            className="px-3 py-2 hover:bg-neutral-100 flex justify-between items-center group">
                            <button
                              className="flex-1 text-left text-sm text-neutral-700 hover:text-primary-600 truncate pr-2"
                              onClick={() => handleLoadPrompt(prompt)}
                              title={`Load: ${prompt.name}\n\"${prompt.prompt_text}\"`}>
                              {prompt.name}
                            </button>
                            <button
                              onClick={() => handleDeletePrompt(prompt.id)}
                              className="text-neutral-400 hover:text-support-red-500 ml-2 opacity-0 group-hover:opacity-100 transition-opacity"
                              aria-label={`Delete prompt ${prompt.name}`}
                              title="Delete Prompt">
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="px-3 py-3 text-sm text-neutral-500 text-center">No saved prompts yet.</p>
                    )}
                  </div>
                )}

                <div>
                  <label htmlFor="prompt-name" className="block text-sm font-medium text-neutral-700 mb-1.5">
                    Save Current Prompt
                  </label>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 space-y-2 sm:space-y-0">
                    <input
                      type="text"
                      id="prompt-name"
                      value={promptName}
                      onChange={(e) => setPromptName(e.target.value)}
                      placeholder="Enter name to save prompt"
                      disabled={isLoading || isSavingPrompt}
                      className="block flex-1 rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-neutral-50"
                    />
                    <Button
                      type="button"
                      onClick={handleSavePrompt}
                      disabled={isLoading || isSavingPrompt || !promptName.trim() || !customPrompt.trim()}
                      variant="secondary"
                      className="inline-flex items-center justify-center px-3 py-1.5 text-sm whitespace-nowrap w-full sm:w-auto"
                      style={{ minWidth: "80px" }}>
                      {isSavingPrompt ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-1 h-4 w-4 text-primary-700"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 24 24">
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>Saving...</span>
                        </>
                      ) : showSaveSuccess ? (
                        <>
                          <CheckIcon className="h-4 w-4 mr-1 text-support-green-500" />
                          <span>Saved!</span>
                        </>
                      ) : (
                        <>
                          <BookmarkIcon className="h-4 w-4 mr-1" />
                          <span>Save</span>
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="mt-auto pt-3">
            <div className="bg-support-red-50 text-support-red-800 p-3 rounded-md border border-support-red-200 text-sm flex items-start">
              <XIcon className="h-4 w-4 mr-2 mt-0.5 text-support-red-600 flex-shrink-0" />
              <span>{error}</span>
              <button
                onClick={() => setError("")}
                className="ml-auto text-support-red-600 hover:text-support-red-800"
                aria-label="Dismiss error">
                <XIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="lg:col-span-2 bg-white rounded-lg shadow-sm p-6 flex flex-col">
        {/* Tab Navigation */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-6">
            <button
              onClick={() => setActiveContentTab("content")}
              className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                activeContentTab === "content"
                  ? "bg-primary-50 text-primary-700 border border-primary-200"
                  : "text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50"
              }`}
            >
              <DocumentTextIcon className="h-4 w-4 mr-2" />
              Generated Content
            </button>
            
            <button
              onClick={() => setActiveContentTab("quotes")}
              className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                activeContentTab === "quotes"
                  ? "bg-primary-50 text-primary-700 border border-primary-200"
                  : "text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50"
              }`}
            >
              <ChatIcon className="h-4 w-4 mr-2" />
              Quote Cards
            </button>
          </div>

          {/* Actions for Content Tab */}
          {activeContentTab === "content" && generatedContent && !isLoading && (
            <div className="flex items-center space-x-2">
              <Button
                type="button"
                onClick={toggleEditMode}
                variant="secondary"
                size="sm"
                className="inline-flex items-center">
                {isEditMode ? "Preview" : "Edit"}
              </Button>

              <Button
                type="button"
                onClick={handleCopyToClipboard}
                disabled={showCopySuccess}
                variant="secondary"
                size="sm"
                className="p-1.5"
                title={showCopySuccess ? "Copied!" : "Copy Markdown"}>
                {showCopySuccess ? (
                  <CheckIcon className="h-5 w-5 text-support-green-500" />
                ) : (
                  <ClipboardCopyIcon className="h-5 w-5" />
                )}
              </Button>

              <div className="relative">
                <Button
                  type="button"
                  onClick={() => setShowActionsDropdown(!showActionsDropdown)}
                  variant="secondary"
                  size="sm"
                  className="p-1.5"
                  title="More actions">
                  <DotsVerticalIcon className="h-5 w-5" />
                </Button>
                {showActionsDropdown && (
                  <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                    <div className="py-1" role="menu" aria-orientation="vertical">
                      <button
                        onClick={() => {
                          handleDownload();
                          setShowActionsDropdown(false);
                        }}
                        className="flex items-center w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100 hover:text-neutral-900"
                        role="menuitem">
                        <DownloadIcon className="h-5 w-5 mr-3" aria-hidden="true" />
                        Download (.md)
                      </button>
                      {isEditMode && (
                        <button
                          onClick={() => {
                            revertChanges();
                            setShowActionsDropdown(false);
                          }}
                          disabled={generatedContent === originalContent}
                          className="flex items-center w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100 hover:text-neutral-900 disabled:opacity-50 disabled:cursor-not-allowed"
                          role="menuitem">
                          <RefreshIcon className="h-5 w-5 mr-3" aria-hidden="true" />
                          Reset Changes
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Tab Content */}
        <div className="flex-grow flex flex-col min-h-[300px]">
          {activeContentTab === "content" && (
            <>
              {isLoading ? (
                <div className="flex-grow flex flex-col justify-center items-center text-center p-12">
                  <svg
                    className="animate-spin h-10 w-10 text-primary-600 mb-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <p className="text-primary-600 font-medium">Generating content...</p>
                  <p className="text-neutral-500 text-sm mt-1">This may take a few moments.</p>
                </div>
              ) : generatedContent ? (
                isEditMode ? (
                  <textarea
                    value={generatedContent}
                    onChange={(e) => setGeneratedContent(e.target.value)}
                    className="flex-grow w-full rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm resize-y"
                    placeholder="Edit your content here..."
                    rows={20}
                  />
                ) : (
                  <div className="prose prose-sm sm:prose max-w-none flex-grow overflow-y-auto p-4 rounded border border-neutral-200 bg-neutral-50 scrollbar-thin scrollbar-thumb-neutral-300 hover:scrollbar-thumb-neutral-400 scrollbar-track-neutral-100">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      className="prose-headings:font-display 
                      prose-h1:text-3xl prose-h1:font-semibold prose-h1:text-neutral-900 prose-h1:mb-6
                      prose-h2:text-2xl prose-h2:font-semibold prose-h2:text-neutral-900 prose-h2:mb-4 prose-h2:mt-8
                      prose-h3:text-xl prose-h3:font-medium prose-h3:text-neutral-900 prose-h3:mb-3 prose-h3:mt-6
                      prose-h4:text-lg prose-h4:font-medium prose-h4:text-neutral-900 prose-h4:mb-2 prose-h4:mt-4
                      prose-p:text-neutral-700 prose-p:text-base prose-p:mb-4 
                      prose-ul:text-neutral-700 prose-ul:mb-4 prose-ol:text-neutral-700 prose-ol:mb-4
                      prose-li:text-neutral-700 prose-li:text-base prose-li:mb-1
                      prose-a:text-primary-600 hover:prose-a:text-primary-700 prose-a:transition-colors
                      prose-strong:text-neutral-800 prose-strong:font-semibold
                      prose-code:text-secondary-700 prose-code:bg-secondary-50 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-sm
                      prose-blockquote:border-l-4 prose-blockquote:border-primary-300 prose-blockquote:pl-4 prose-blockquote:text-neutral-600 prose-blockquote:italic prose-blockquote:my-4
                      prose-hr:border-neutral-200 prose-hr:my-6">
                      {generatedContent}
                    </ReactMarkdown>
                  </div>
                )
              ) : (
                <div className="flex-grow flex flex-col justify-center items-center text-center p-12 border border-dashed border-neutral-300 rounded-md bg-neutral-50">
                  <SparklesIcon className="h-10 w-10 text-neutral-400 mb-3" />
                  <p className="text-neutral-500 text-sm">Generated content will appear here.</p>
                  <p className="text-neutral-400 text-xs mt-1">Use the controls to generate content from this episode.</p>
                </div>
              )}
            </>
          )}

          {activeContentTab === "quotes" && (
            <div className="flex-grow">
              <QuoteCard />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
